/**
 * @fileoverview CategoryManagement Component
 *
 * Main category management interface allowing users to add, edit, cease,
 * reactivate, and delete budget categories. Includes validation, permission
 * checks, and modal interfaces for adding and editing categories.
 *
 * @module components/CategoryManagement/CategoryManagement
 */
import { useState } from 'react';
import { getTodaysDate, getFirstDayOfMonth, formatDateForDisplay } from '../../utils/dateUtils';
import { monthAPI, categoryAPI } from '../../services/api';
import CategoryAddForm from './CategoryAddForm';
import CategoryTable from './CategoryTable';
import EditCategoryModal from './EditCategoryModal';
import SortCategoriesModal from './SortCategoriesModal';
import styles from './CategoryManagement.module.css';

const CategoryManagement = ({
    categories,
    onAddCategory,
    onCeaseCategory,
    onReactivateCategory,
    onDeleteCategory,
    onUpdateCategory,
    onReloadCategories,
    userRole,
    budgetStartMonth,
    _showCategoryManagement,
    _onToggle,
    onCategoryBalancesRefresh,
    readOnly = false
}) => {
    const [showAddForm, setShowAddForm] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [startDate, setStartDate] = useState(getFirstDayOfMonth(getTodaysDate()));
    const [startingBalance, setStartingBalance] = useState('');
    const [allowExpense, setAllowExpense] = useState(false);
    const [allowTransfer, setAllowTransfer] = useState(false);
    const [validationError, setValidationError] = useState('');
    const [error, setError] = useState('');
    const [editingCategory, setEditingCategory] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [hasClosedMonths, setHasClosedMonths] = useState(false);
    const [isSortModalOpen, setIsSortModalOpen] = useState(false);

    const canManageCategories = userRole === 'owner' || userRole === 'elevated_member';

    const isActive = (category) => {
        if (!category.endDate) return true;
        return new Date(category.endDate) > new Date();
    };

    const isFutureDated = (category) => {
        const startDate = new Date(category.startDate);
        const currentDate = new Date();
        return startDate > currentDate;
    };

    // Handle start date change - normalize to first of month and validate
    const handleStartDateChange = (value) => {
        const normalizedDate = getFirstDayOfMonth(value);
        setStartDate(normalizedDate);

        // Real-time validation against budget start date
        if (budgetStartMonth) {
            const budgetStartFirstDay = getFirstDayOfMonth(budgetStartMonth);

            // Compare as strings to avoid timezone issues
            if (normalizedDate < budgetStartFirstDay) {
                setError(`Category start date cannot be before budget start date (${formatDateForDisplay(budgetStartMonth)})`);
            } else {
                // Clear error if date is now valid
                setError('');
            }
        }
    };

    const handleAddCategory = async () => {
        if (!newCategoryName.trim()) {
            setError('Category name is required');
            return;
        }

        // Validate that at least one transaction type is selected
        if (!allowExpense && !allowTransfer) {
            setValidationError('Please select at least one transaction type');
            return;
        }

        // Clear validation error if valid
        setValidationError('');

        // Validate start date is not before budget start date
        if (budgetStartMonth) {
            const budgetStart = new Date(budgetStartMonth);
            const categoryStart = new Date(startDate);

            if (categoryStart < budgetStart) {
                setError(`Category start date cannot be before budget start date (${formatDateForDisplay(budgetStartMonth)})`);
                return;
            }
        }

        // Validate starting balance if provided
        if (startingBalance && isNaN(parseFloat(startingBalance))) {
            setError('Starting balance must be a valid number');
            return;
        }

        try {
            // Only pass starting balance if start date matches budget start date
            const categoryStartsWithBudget = startDate === getFirstDayOfMonth(budgetStartMonth);
            const balanceToSend = categoryStartsWithBudget && startingBalance ? parseFloat(startingBalance) : null;

            await onAddCategory(newCategoryName.trim(), startDate, balanceToSend, allowExpense, allowTransfer);
            setNewCategoryName('');
            setStartDate(getFirstDayOfMonth(getTodaysDate()));
            setStartingBalance('');
            setAllowExpense(false);
            setAllowTransfer(false);
            setShowAddForm(false);
            setError('');
            setValidationError('');

            // Refresh category balances to show the new category's balance
            if (onCategoryBalancesRefresh) {
                onCategoryBalancesRefresh();
            }
        } catch (err) {
            setError(err.message);
        }
    };

    const handleCeaseCategory = async (categoryKey, categoryName) => {
        const confirmed = window.confirm(
            `Are you sure you want to cease "${categoryName}"?\n\n` +
            `This will cease the category immediately with today's date.`
        );

        if (!confirmed) return;

        try {
            await onCeaseCategory(categoryKey);
            setError('');
        } catch (err) {
            setError(err.message);
        }
    };

    const handleReactivateCategory = async (categoryKey) => {
        try {
            await onReactivateCategory(categoryKey);
            setError('');
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDeleteCategory = async (categoryKey, categoryName) => {
        const confirmed = window.confirm(
            `Are you sure you want to delete "${categoryName}"?\n\n` +
            `This action cannot be undone.`
        );

        if (!confirmed) return;

        try {
            await onDeleteCategory(categoryKey);
            setError('');
        } catch (err) {
            setError(err.message);
        }
    };

    const handleEditCategory = async (category) => {
        setEditingCategory(category);
        setIsEditModalOpen(true);

        // Check if there are any closed months from this category's start date forward
        try {
            const hasClosed = await monthAPI.hasClosedMonthsFrom(category.startDate);
            setHasClosedMonths(hasClosed);
        } catch (err) {
            // If check fails, assume no closed months
            setHasClosedMonths(false);
        }
    };

    const handleSaveEdit = async (updatedCategory) => {
        try {
            await onUpdateCategory(
                updatedCategory.categoryKey,
                updatedCategory.categoryName,
                updatedCategory.startingBalance,
                updatedCategory.allowExpense,
                updatedCategory.allowTransfer
            );
            setIsEditModalOpen(false);
            setEditingCategory(null);
            setError('');

            // Refresh category balances if starting balance was updated
            if (onCategoryBalancesRefresh) {
                onCategoryBalancesRefresh();
            }
        } catch (err) {
            setError(err.message);
        }
    };

    const handleCloseEditModal = () => {
        setIsEditModalOpen(false);
        setEditingCategory(null);
        setHasClosedMonths(false);
    };

    const handleSaveSortOrder = async (updates) => {
        try {
            await categoryAPI.updateSortOrder(updates);
            setError('');
            // Reload categories to show new sort order
            if (onReloadCategories) {
                await onReloadCategories();
            }
        } catch (err) {
            throw err; // Re-throw to let modal handle the error
        }
    };

    return (
        <div className={styles.container}>
            <>
                    {!canManageCategories && (
                        <p className={styles.permissionMessage}>
                            Only budget owners and elevated members can manage categories.
                        </p>
                    )}

                    {error && (
                        <div className={styles.error}>
                            {error}
                        </div>
                    )}

                    {canManageCategories && !readOnly && (
                        <div className={styles.addSection}>
                            <div className={styles.buttonRow}>
                                {!showAddForm ? (
                                    <button className={styles.addButton} onClick={() => setShowAddForm(true)}>
                                        Add New Category
                                    </button>
                                ) : (
                                    <CategoryAddForm
                                        newCategoryName={newCategoryName}
                                        setNewCategoryName={setNewCategoryName}
                                        startDate={startDate}
                                        handleStartDateChange={handleStartDateChange}
                                        startingBalance={startingBalance}
                                        setStartingBalance={setStartingBalance}
                                        budgetStartMonth={budgetStartMonth}
                                        handleAddCategory={handleAddCategory}
                                        setShowAddForm={setShowAddForm}
                                        setError={setError}
                                        allowExpense={allowExpense}
                                        setAllowExpense={setAllowExpense}
                                        allowTransfer={allowTransfer}
                                        setAllowTransfer={setAllowTransfer}
                                        validationError={validationError}
                                    />
                                )}
                                <button
                                    className={styles.sortButton}
                                    onClick={() => setIsSortModalOpen(true)}
                                >
                                    Edit Sort Order
                                </button>
                            </div>
                        </div>
                    )}

                    <CategoryTable
                        categories={categories}
                        canManageCategories={canManageCategories}
                        isActive={isActive}
                        isFutureDated={isFutureDated}
                        handleCeaseCategory={handleCeaseCategory}
                        handleReactivateCategory={handleReactivateCategory}
                        handleDeleteCategory={handleDeleteCategory}
                        handleEditCategory={handleEditCategory}
                        readOnly={readOnly}
                    />

                    <EditCategoryModal
                        isOpen={isEditModalOpen}
                        onClose={handleCloseEditModal}
                        category={editingCategory}
                        onSave={handleSaveEdit}
                        hasClosedMonths={hasClosedMonths}
                    />

                    <SortCategoriesModal
                        isOpen={isSortModalOpen}
                        onClose={() => setIsSortModalOpen(false)}
                        categories={categories}
                        onSave={handleSaveSortOrder}
                    />
            </>
        </div>
    );
};

export default CategoryManagement;